import { usePathname } from "next/navigation";
import { fun } from "../lib/request";
import { useUser } from "@/stores/appStore";
import { api } from "@/lib/env";
import pkg from "@/package.json";
import { useCallback, useRef } from "react";

export type EventLogType = {
  platform?: 'web' | 'cx';
  element: string;
  event?: 'click';
  path?: string;
};

export type TraceConfig = {
  scene: string;
};

export type TraceContent = { [key: string]: any };

export const useTrace = () => {
  const { refreshUserInfo } = useUser();
  const pathname = usePathname();
  const traceIdRef = useRef('');

  const record = useCallback(async (level: string = 'log', content: TraceContent) => {
    const user = await refreshUserInfo();
    try {
      return await fun.post<{}>(
        api('/common/recordLog', true),
        {
          platform: 'web',
          content: JSON.stringify(content),
          level,
          scene: 'common',
          trace_id: `${user?.user?.userId ?? '??'}/${traceIdRef.current || Date.now().toString()}`,
          version: pkg.version,
        },
      );
    } catch (error) {
      console.error('Error query common', error);
      return null;
    }
  }, [refreshUserInfo]);

  const group = useCallback(async (config: TraceConfig) => {
    traceIdRef.current = Date.now().toString();
    return await record('log', {
      title: '[START]',
      scene: config.scene,
    });
  }, [record]);

  const groupEnd = useCallback(async () => {
    const resp = await record('log', {
      title: '[END]'
    });
    traceIdRef.current = '';
    return resp;
  }, [record]);

  const log = useCallback(async (content: TraceContent) => {
    return await record('log', content);
  }, [record]);

  const info = useCallback(async (content: TraceContent) => {
    return await record('info', content);
  }, [record]);

  const warn = useCallback(async (content: TraceContent) => {
    return await record('warn', content);
  }, [record]);

  const error = useCallback(async (content: TraceContent) => {
    return await record('error', content);
  }, [record]);

  const eventLog = useCallback(async (log: EventLogType) => {
    try {
      return await fun.post<{}>(
        api('/common/eventLog', true),
        {
          platform: 'web',
          element: log?.element || '',
          event: log?.event || 'click',
          path: log?.path || pathname,
        },
      );
    } catch (error) {
      console.error('Error query common', error);
      return null;
    }
  }, [pathname]);

  return {
    group,
    groupEnd,
    log,
    info,
    warn,
    error,
    eventLog,
  };
};